import React, { Component, Fragment } from 'react';
import { Container, Button, Form, Dropdown, DropdownButton, Badge } from 'react-bootstrap';
import Alias from "components/controls/alias";
import { GoLock, GoTrash, GoPencil, GoWorkflow } from 'react-icons/go';
import { PiShippingContainer } from "react-icons/pi";
import { GoSync } from "react-icons/go";
import { MdOutlineExpandMore } from "react-icons/md";
import { BsThreeDotsVertical } from "react-icons/bs";
import Select, { components } from 'components/controls/select';
import ConfirmModal from 'components/controls/confirmModal';
import Utils from "context/utils";
import AddDefinitionModal from "./addDefinition";
import Tooltip from "../../controls/common/tooltip";
import CreateSessionModal from "../../debugger/sessions/create";
import { sessionStatusClass, isSessionOpen } from "../session/sessionStatus";
import "./index.css";

// Renders a status badge for any known status, `open` included. Whether the badge is actually
// shown to the user is *not* this component's call — see formatSessionOptionLabel's `context`
// param below, which is where that decision belongs.
//
// Carries its own dedicated class (playground-session-select-option-status) rather than being
// targeted by a `.header .badge` descendant rule: the alias pill (also a react-bootstrap <Badge>,
// via <Alias>) used to sit inside the same header row and matched that same descendant selector,
// picking up spacing meant only for this status badge -- see the CSS comment on that class.
export function SessionStatusHint({ status }) {
  if (!status) {
    return null;
  }
  return (
    <Badge className={`playground-session-select-option-status ${sessionStatusClass(status)}`}>{status}</Badge>
  );
}

// `label` is kept a plain string (the session's alias, falling back to its id) rather than JSX:
// react-select's default filtering stringifies `option.label` via a template literal, so a JSX label
// always coerces to "[object Object]" and breaks typed search over the menu. The alias is the whole
// point of putting it here — typed search now matches the name the user chose instead of a UUID. The
// richer presentation (pill, date, description, status badge) is rendered separately by
// `formatSessionOptionLabel` below, which react-select calls purely for display and never for
// filtering.
export function buildSessionOptions(sessions, currentSessionId) {
  let currentSessionOption = null;
  const sessionOptions = [];
  if (!sessions) {
    return { sessionOptions, currentSessionOption };
  }
  for ( const session of sessions ) {
    const sessionOption = {
      value: session,
      label: Utils.aliasOrId(session)
    };
    if ( session["id"] == currentSessionId ) {
      currentSessionOption = sessionOption;
    }
    sessionOptions.push(sessionOption);
  }
  return { sessionOptions, currentSessionOption };
}

// Presentation-only hook (react-select's `formatOptionLabel`) for the session Select: renders
// the same visuals the option previously carried in its (now string) `label`, without affecting
// filtering. Exported so it can be tested directly, without mounting react-select's portalled
// menu.
//
// react-select calls this as `formatOptionLabel(option, { context })`, where `context` is
// "menu" for rows in the open dropdown and "value" for the selected value shown in the closed
// control. Layout depends ONLY on `context`, never on which option is currently selected — every
// menu row (including the current session's) gets the same id/date/description/status layout,
// and the closed control shows the alias pill when the session has one, otherwise the plain id.
export function formatSessionOptionLabel(option, timezone, context) {
  const session = option.value;
  if ( context === "value" ) {
    // Closed control: plain text, matching the sibling Definitions select, which renders a plain
    // string label rather than the pill. Utils.aliasOrId already gives the alias-or-id text this
    // needs; no <Alias> chip here.
    return ( <span>{Utils.aliasOrId(session)}</span> );
  }
  return (
    <div className="playground-session-select-option">
      <div className="playground-session-select-option-header">
        <div className="playground-session-select-option-iddate">
          <p><code className="playground-session-select-option-id text-muted">{session["id"]}</code></p>
          <p className="playground-session-select-option-date text-muted">
            {Utils.formatDateTime(session["created_at"], timezone)}
          </p>
        </div>
        <SessionStatusHint status={session["status"]} />
      </div>
      {session["alias"] &&
        <p className="playground-session-select-option-alias">
          <span className="playground-session-select-option-alias-label text-muted">Alias</span>
          <Alias value={session["alias"]} />
        </p>
      }
      {session["description"] &&
        <p className="playground-session-select-option-description">{session["description"]}</p>
      }
    </div>
  );
}

export default class Selector extends Component {
  constructor(props) {
    super(props);

    this.state = {
      addDefinitionModalShow: false,
      definitionToBeEdited: null,
      deleteDefinitionModalShow: false,
      createSessionModalShow: false,
      editSessionModalShow: false
    };
  }

  /**
   * Add new
   */

  setAddDefinitionModalShow(show) {
    this.setState({
      addDefinitionModalShow: show,
      definitionToBeEdited: null
    });
  }

  handleAddDefinition(definition) {
    this.props.shell.shellInterface.updateProgress(true, "Creating new playground definition...", false, null);

    const layout = (definition.layoutType === "grid")
      ? {
          type: "grid",
          columns: definition.columns,
          row_height: definition.rowHeight,
          vertical_sizing: definition.verticalSizing,
          reference_rows: definition.referenceRows,
        }
      : { type: "free_form" };

    const body = {
      engine: "workflow_blobhub",
      command: "create_definition",
      category: "playground",
      alias: definition.alias,
      layout,
    };
    this.props.app.api.revisionsIdDataCommandPost(this.props.revision.id, body)
      .then( response => {
        // Remove progress
        this.props.shell.shellInterface.updateProgress(false, null, false, null);

        // Retrieve definition
        const definition = response.data["definition"];

        // Re-load definitions
        this.props.shell.shellInterface.reloadDefinitions(definition.id);
      }, error => {
        // Render error state
        this.props.shell.shellInterface.updateProgress(
          false, null, true, "Failed to create playground definition: " + error.message);
      });
  }

  /**
   * Edit
   */

  setEditDefinitionModalShow(show) {
    this.setState({
      addDefinitionModalShow: show,
      definitionToBeEdited: show ? this.props.shell.definition : null
    });
  }

  handleEditDefinition(definition) {
    this.props.shell.shellInterface.updateProgress(true, "Applying playground definition changes...", false, null);

    const body = {
      engine: "workflow_blobhub",
      command: "update_definition",
      definition_id: definition.id,
      alias: definition.alias
    };
    this.props.app.api.revisionsIdDataCommandPost(this.props.revision.id, body)
      .then( response => {
        // Remove progress
        this.props.shell.shellInterface.updateProgress(false, null, false, null);

        // Re-load definitions
        this.props.shell.shellInterface.reloadDefinitions(definition.alias);
      }, error => {
        // Render error state
        this.props.shell.shellInterface.updateProgress(
          false, null, true, "Failed to update playground definition: " + error.message);
      });
  }

  /**
   * Delete
   */

  setDeleteDefinitionModalShow(show) {
    this.setState({deleteDefinitionModalShow: show});
  }

  handleDeleteDefinition() {
    this.props.shell.shellInterface.updateProgress(true, "Deleting playground definition...", false, null);

    const definition = this.props.shell.definition;
    const body = {
      engine: "workflow_blobhub",
      command: "delete_definition",
      definition_id: definition["id"]
    };
    this.props.app.api.revisionsIdDataCommandPost(this.props.revision.id, body)
      .then( response => {
        // Remove progress
        this.props.shell.shellInterface.updateProgress(false, null, false, null);

        // Re-load definitions
        this.props.shell.shellInterface.reloadDefinitions(null);
      }, error => {
        // Render error state
        this.props.shell.shellInterface.updateProgress(
          false, null, true, "Failed to delete playground definition: " + error.message);
      });
  }

  /**
   * Session
   */

  handleCreateSession(session) {
    this.props.sessionManager.createNewSession(session);
  }

  // Promise contract call site (see CreateSessionModal's docblock): the manager resolves
  // { ok, aliasInUse, message }, so returning its promise directly lets the modal stay open on
  // failure and close only once the update actually lands.
  handleUpdateSession(fields) {
    return this.props.sessionManager.updateSession(fields);
  }

  /** What the Sync switch says about itself: what it does, or what is keeping it on. */
  syncTooltip() {
    const manager = this.props.sessionManager;
    const reasons = (manager && manager.syncHoldReasons) ? manager.syncHoldReasons() : [];
    if (reasons.length > 0) {
      return `Kept on by: ${reasons.join(" · ")}`;
    }
    return manager && manager.sessionSync
      ? "Watching this session for changes. Stops on its own after three quiet minutes."
      : "Watch this session for changes";
  }

  render() {
    const disabled = this.props.shell.modified;

    // Definitions select
    let currentDefinitionOption = null;
    let definitionOptions = [];
    for ( const definition of this.props.shell.definitions ) {
      const definitionOption = {
        value: definition,
        label: definition["alias"]
      };
      definitionOptions.push(definitionOption);
      if ( definition["id"] == this.props.shell.definitionId ) {
        currentDefinitionOption = definitionOption;
      }
    }

    // Sessions select
    const { sessionOptions, currentSessionOption } = buildSessionOptions(
      this.props.sessionManager.sessions, this.props.sessionManager.sessionId);

    // This one is used to hide controls in parallel with `disabled`. They never interfere.
    const canEdit = this.props.shell.shellInterface.canEditRevision();

    return (
      <>
        <Container className="blob-header-row content-row workflow-selector-container">
          <div className="blob-download-row workflow-selector-row playground-selector-row">
            {0 != this.props.shell.definitions.length &&
              <>
                <span>
                  Playground&nbsp;&nbsp;&nbsp;
                </span>
                <span>
                  <div className="playground-selector-dropdown">
                    <Select
                      isDisabled={disabled}
                      options={definitionOptions}
                      value={currentDefinitionOption}
                      onChange={(option) => {
                        this.props.shell.shellInterface.switchDefinition(option.value);
                      }}
                      menuPortalTarget={document.body}                    />
                  </div>
                </span>
                <span>
                  &nbsp;&nbsp;&nbsp;
                  {canEdit &&
                    <Dropdown className="d-inline-block">
                      <Dropdown.Toggle
                        variant="light"
                        size="sm"
                        className="align-middle playground-action-dropdown-toggle"
                        disabled={disabled}
                      >
                        <BsThreeDotsVertical />
                      </Dropdown.Toggle>
                      <Dropdown.Menu popperConfig={{ strategy: "fixed" }}>
                        <Dropdown.Item onClick={() => this.setAddDefinitionModalShow(true)}>
                          <GoWorkflow />&nbsp;&nbsp;New Playground
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => this.setEditDefinitionModalShow(true)}>
                          <GoPencil />&nbsp;&nbsp;Edit Playground
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => this.setDeleteDefinitionModalShow(true)}>
                          <GoTrash />&nbsp;&nbsp;Delete Playground
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={() => this.props.shell.shellInterface.getSync().loadPrerequisites()}>
                          <GoSync />&nbsp;&nbsp;Refresh Workflows
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  }
                </span>
              </>
            }
            {0 == this.props.shell.definitions.length &&
              <span>
                Create the first playground definition here.
              </span>
            }

            <span className="float-right">
              <span>
                Session&nbsp;&nbsp;&nbsp;
              </span>
              <span>
                <div className="session-selector-dropdown">
                  <Select
                    isDisabled={disabled}
                    options={sessionOptions}
                    value={currentSessionOption}
                    formatOptionLabel={(option, { context }) =>
                      formatSessionOptionLabel(option, this.props.timezone, context)}
                    onChange={(option) => {
                      const session = option.value;
                      this.props.shell.shellInterface.sessionSelected(session);
                    }}
                    menuPortalTarget={document.body}                  />
                </div>
              </span>
              <span>
                &nbsp;&nbsp;&nbsp;
                <Dropdown className="d-inline-block">
                  <Dropdown.Toggle
                    variant="light"
                    size="sm"
                    className="align-middle playground-action-dropdown-toggle"
                    disabled={disabled}
                  >
                    <BsThreeDotsVertical />
                  </Dropdown.Toggle>
                  <Dropdown.Menu popperConfig={{ strategy: "fixed" }}>
                    {this.props.shell.shellInterface.canEditRevision() &&
                      <Dropdown.Item onClick={() => {
                          this.setState({ createSessionModalShow: true });
                        }}>
                        <PiShippingContainer />&nbsp;&nbsp;New Session
                      </Dropdown.Item>
                    }
                    {/* Shown only for a selected, open session that the reviser can edit:
                      * update_session refuses a session that is not open, so a closed session
                      * shows no Edit Session entry at all. */}
                    {this.props.sessionManager.session && this.props.shell.shellInterface.canEditRevision() &&
                      isSessionOpen(this.props.sessionManager.session) &&
                      <Dropdown.Item onClick={() => {
                          this.setState({ editSessionModalShow: true });
                        }}>
                        <GoPencil />&nbsp;&nbsp;Edit Session
                      </Dropdown.Item>
                    }
                    <Dropdown.Item onClick={() => {
                        this.props.sessionManager.loadSessions(false);
                      }}>
                      <GoSync />&nbsp;&nbsp;Refresh Sessions
                    </Dropdown.Item>
                    {this.props.sessionManager.lastSessionId &&
                      <Dropdown.Item onClick={() => {
                          this.props.sessionManager.loadSessions(true);
                        }}>
                        <MdOutlineExpandMore />&nbsp;&nbsp;Load More Sessions
                      </Dropdown.Item>
                    }
                  </Dropdown.Menu>
                </Dropdown>

                &nbsp;&nbsp;&nbsp;
                {/* The switch says WHY it is on. Widgets hold sync open while they are waiting on
                    something — an execution running, cells the orchestrator has yet to move — and a
                    switch that will not settle is alarming until you can read the reason for it.
                    Turning it off still turns it off: a hold cannot overrule the person, only the
                    idle clock. */}
                <Tooltip text={this.syncTooltip()}>
                  <span>
                    <Form.Check
                      id="playground-session-sync-switch"
                      type="switch" label="Sync" inline
                      disabled={disabled}
                      checked={this.props.sessionManager.sessionSync}
                      onChange={(event) => {
                        this.props.shell.shellInterface.enableSessionSync(event.target.checked);
                      }}
                    />
                  </span>
                </Tooltip>
              </span>
            </span>
          </div>
        </Container>

        <AddDefinitionModal
          show={this.state.addDefinitionModalShow}
          onHide={() => this.setAddDefinitionModalShow(false)}
          handleAddDefinition={(definition) => this.handleAddDefinition(definition)}
          handleEditDefinition={(definition) => this.handleEditDefinition(definition)}
          definition={this.state.definitionToBeEdited}
        />

        {this.props.shell.definition &&
          <ConfirmModal
            show={this.state.deleteDefinitionModalShow}
            onHide={() => this.setDeleteDefinitionModalShow(false)}
            onConfirm={() => this.handleDeleteDefinition()}
            title="Delete Playground Definition"
            body={
              <>
                Are you sure that you would like to delete definition{" "}
                <Alias value={this.props.shell.definition.alias} />?
              </>
            }
          />
        }

        <CreateSessionModal
          show={this.state.createSessionModalShow}
          onHide={() => this.setState({ createSessionModalShow: false })}
          handleCreate={(session) => this.handleCreateSession(session)}
        />

        {/* A second, separately-named instance rather than merging with the create modal above --
          * that one's fire-and-forget handleCreateSession() closes it immediately (see
          * CreateSessionModal's docblock), which would be ambiguous to share with the edit path's
          * Promise contract. Guarded on the current session existing, matching the debugger's Edit
          * affordance (debugger/session/index.js). */}
        {this.props.sessionManager.session &&
          <CreateSessionModal
            session={this.props.sessionManager.session}
            show={this.state.editSessionModalShow}
            onHide={() => this.setState({ editSessionModalShow: false })}
            handleCreate={(fields) => this.handleUpdateSession(fields)}
          />
        }
      </>
    );
  }
}
